import { useState } from "react";
import { useTranslation } from "react-i18next";

import { usePlatformSetKycStatus } from "@/lib/platform";

const CHOICE_VALUES = ["unverified", "pending", "verified", "rejected"] as const;

export function KycStatusModal({
  orgId,
  currentStatus,
  onClose,
}: {
  orgId: number | string;
  currentStatus: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [status, setStatus] = useState(currentStatus);
  const [reason, setReason] = useState("");
  const mutation = usePlatformSetKycStatus(orgId);

  return (
    <div
      className="modal-backdrop bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="kyc-modal-heading"
    >
      <form
        className="w-full max-w-md rounded-lg border border-nb-border bg-nb-surface p-6 shadow-xl"
        onSubmit={async (e) => {
          e.preventDefault();
          if (status === currentStatus) {
            onClose();
            return;
          }
          await mutation.mutateAsync({ kyc_status: status, reason });
          onClose();
        }}
      >
        <h2 id="kyc-modal-heading" className="text-lg font-semibold">
          {t("ui.kycStatusModal.title")}
        </h2>
        <fieldset className="mt-3 space-y-1.5">
          {CHOICE_VALUES.map((value) => (
            <label key={value} className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="kyc_status"
                value={value}
                checked={status === value}
                onChange={() => setStatus(value)}
                data-testid={`kyc-radio-${value}`}
              />
              <span>{t(`ui.kycStatusModal.choice_${value}`)}</span>
            </label>
          ))}
        </fieldset>
        <label className="mt-4 block text-sm">
          <span className="text-nb-mute">{t("ui.kycStatusModal.reasonLabel")}</span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            className="mt-1 w-full rounded-md border border-nb-border bg-nb-bg p-2 text-sm"
            data-testid="kyc-reason"
            placeholder={t("ui.kycStatusModal.reasonPlaceholder")}
          />
        </label>
        {mutation.isError && (
          <div className="mt-2 text-sm text-nb-danger" role="alert">
            {t("ui.kycStatusModal.errorGeneric")}
          </div>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose}>
            {t("ui.kycStatusModal.cancel")}
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={mutation.isPending}
            data-testid="kyc-submit"
          >
            {mutation.isPending ? t("ui.kycStatusModal.saving") : t("ui.kycStatusModal.save")}
          </button>
        </div>
      </form>
    </div>
  );
}
